import React, { useState } from 'react';
import { Mail, Sparkles, Loader2, Check, ArrowRight, Copy, Share2, Gift } from 'lucide-react';
import { readStoredReferral } from '../hooks/useCaptureReferral';

import { trackLead } from '../lib/pixel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ReferralShare({ url, testIdPrefix }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch { /* clipboard not permitted */ }
  };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Little Splash Swim',
          text: 'Little Splash is giving away a free lesson — grab yours!',
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      copy();
    }
  };
  return (
    <div
      data-testid={`${testIdPrefix}-referral`}
      className="mt-4 rounded-xl p-4 bg-white border border-splash-coral/30"
    >
      <div className="flex items-center gap-2 mb-2">
        <Gift className="w-4 h-4 text-splash-coral shrink-0" />
        <div className="text-[11px] uppercase tracking-widest font-semibold text-splash-coralDark">
          Give &amp; Get
        </div>
      </div>
      <div className="text-sm font-semibold text-splash-green">
        Refer a friend, earn another free lesson.
      </div>
      <div className="mt-2 text-xs text-splash-green/60">
        Share your link. When they sign up, you both get a free lesson.
      </div>
      <div
        data-testid={`${testIdPrefix}-referral-url`}
        onClick={(e) => {
          try {
            const range = document.createRange();
            range.selectNodeContents(e.currentTarget);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
          } catch { /* noop */ }
        }}
        className="mt-3 rounded-xl px-3 py-2 text-[11px] sm:text-xs font-mono break-all leading-relaxed cursor-text bg-splash-cream border border-splash-sand text-splash-green"
      >
        {url}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={copy}
          data-testid={`${testIdPrefix}-referral-copy`}
          className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold btn-transition bg-splash-teal text-white hover:bg-splash-tealDark"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
        <button
          type="button"
          onClick={share}
          data-testid={`${testIdPrefix}-referral-share`}
          className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold btn-transition bg-splash-coral text-white hover:bg-splash-coralDark"
        >
          <Share2 className="w-3.5 h-3.5" /> Share
        </button>
      </div>
    </div>
  );
}

/**
 * SubscribeForm
 * -------------
 * Email opt-in that unlocks a "First Lesson Free" promo code when a family
 * signs up for a group lesson session. Renders in three visual variants so it
 * can drop naturally into a footer, an inline homepage section, or a compact
 * banner.
 *
 * Props:
 *   variant   -> 'inline' (default, full card) | 'compact' (footer/banner) | 'stacked'
 *   source    -> analytics tag stored server-side (e.g. 'footer', 'home_banner', 'programs')
 *   testIdPrefix -> optional prefix so multiple instances on one page stay unique
 */
export default function SubscribeForm({ variant = 'inline', source = 'website', testIdPrefix = 'subscribe' }) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [result, setResult] = useState(null); // { promo_code, already }
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const resp = await fetch(`${API}/subscribers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          source,
          referred_by: readStoredReferral(),
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Something went wrong. Please try again.');
      }
      const data = await resp.json();
      setResult({
        promo_code: data.promo_code,
        referral_url: data.referral_url,
        already: data.status === 'already_subscribed',
      });
      setStatus('success');
      trackLead({ content_name: 'Email signup', source });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Network error. Please try again.');
    }
  };

  // ---------- Success state (shared for all variants) ----------
  if (status === 'success' && result) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className="rounded-2xl p-5 sm:p-6 bg-splash-teal/10 border border-splash-teal/30 text-splash-green"
      >
        <div className="flex items-start gap-3">
          <div className="shrink-0 w-9 h-9 rounded-full grid place-items-center bg-splash-teal text-white">
            <Check className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg leading-tight">
              {result.already ? "You're already on the list!" : "You're in — First Lesson Free unlocked!"}
            </div>
            <div className="mt-1 text-sm text-splash-green/75">
              {result.already
                ? "We resent your promo code details to your inbox as a reminder."
                : "Check your email — we sent your welcome note and code."}
            </div>
            <div className="mt-3 rounded-xl border-2 border-dashed border-splash-teal bg-white px-4 py-3 text-center">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-splash-teal">
                Your Promo Code
              </div>
              <div
                data-testid={`${testIdPrefix}-promo-code`}
                className="mt-1 font-mono font-bold text-xl text-splash-green break-all"
              >
                {result.promo_code}
              </div>
            </div>
            <div className="mt-2 text-xs text-splash-green/60">
              Redeemable at your first group lesson session.
            </div>

            {result.referral_url && (
              <ReferralShare url={result.referral_url} testIdPrefix={testIdPrefix} />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---------- Compact variant (footer / banner) ----------
  if (variant === 'compact') {
    return (
      <form onSubmit={submit} data-testid={`${testIdPrefix}-form`} className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-splash-green/50" />
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              data-testid={`${testIdPrefix}-email`}
              className="w-full rounded-full bg-white pl-9 pr-4 py-2.5 text-sm text-splash-green placeholder:text-splash-green/40 focus:outline-none focus:ring-2 focus:ring-splash-coral"
            />
          </div>
          <button
            type="submit"
            disabled={status === 'loading'}
            data-testid={`${testIdPrefix}-submit`}
            className="inline-flex items-center justify-center gap-1.5 rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold btn-transition whitespace-nowrap"
          >
            {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            Get Free Lesson
          </button>
        </div>
        {status === 'error' && (
          <div className="text-xs text-splash-coral" data-testid={`${testIdPrefix}-error`}>{errorMsg}</div>
        )}
      </form>
    );
  }

  // ---------- Inline variant (full card) ----------
  return (
    <form
      onSubmit={submit}
      data-testid={`${testIdPrefix}-form`}
      className="space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-splash-green/70">First name (optional)</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="Alex"
            data-testid={`${testIdPrefix}-first-name`}
            className="mt-1.5 w-full rounded-full bg-white border border-splash-sand px-4 py-3 text-splash-green focus:outline-none focus:ring-2 focus:ring-splash-coral"
          />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-widest font-semibold text-splash-green/70">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@email.com"
            data-testid={`${testIdPrefix}-email`}
            className="mt-1.5 w-full rounded-full bg-white border border-splash-sand px-4 py-3 text-splash-green focus:outline-none focus:ring-2 focus:ring-splash-coral"
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={status === 'loading'}
        data-testid={`${testIdPrefix}-submit`}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark disabled:opacity-60 text-white px-6 py-3 font-semibold btn-transition"
      >
        {status === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
        Claim Free Lesson <ArrowRight className="w-4 h-4" />
      </button>
      {status === 'error' && (
        <div className="text-sm text-splash-coral" data-testid={`${testIdPrefix}-error`}>{errorMsg}</div>
      )}
      <div className="text-xs text-splash-green/60">
        No spam. Discounts, program updates, and your first group lesson free when you enroll.
      </div>
    </form>
  );
}
